/**
 * Cliente API genérico para todas las requests REST del portal.
 * Incluye manejo de errores, interceptores y configuración base.
 */

export interface ApiConfig {
  baseURL: string;
  timeout?: number;
  headers?: Record<string, string>;
}

export interface ApiResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
  headers: Record<string, string>;
}

export interface ApiError {
  message: string;
  status: number;
  statusText: string;
  data?: any;
}

export class ApiClient {
  private baseURL: string;
  private timeout: number;
  private defaultHeaders: Record<string, string>;

  constructor(config: ApiConfig) {
    this.baseURL = config.baseURL.replace(/\/$/, ''); // Remove trailing slash
    this.timeout = config.timeout || 10000; // 10 seconds default
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      // 'ngrok-skip-browser-warning': '69420',
      ...config.headers,
    };
  }

  /**
   * Realizar una request HTTP genérica
   */
  private async request<T>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    endpoint: string,
    options: {
      body?: any;
      headers?: Record<string, string>;
      queryParams?: Record<string, string>;
    } = {}
  ): Promise<ApiResponse<T>> {
    const { body, headers = {}, queryParams = {} } = options;

    // Construir URL con query parameters
    const url = new URL(`${this.baseURL}${endpoint}`);
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.append(key, value);
      }
    });

    // Configurar headers
    const finalHeaders: Record<string, string> = {
      ...this.defaultHeaders,
      ...headers,
    };

    // Configurar request
    const requestConfig: RequestInit = {
      method,
      headers: finalHeaders,
      signal: AbortSignal.timeout(this.timeout),
    };

    // Agregar body si es necesario
    if (body && ['POST', 'PUT', 'PATCH'].includes(method)) {
      requestConfig.body = JSON.stringify(body);
    }

    try {
      const response = await fetch(url.toString(), requestConfig);

      let responseData: T;
      const contentType = response.headers.get('content-type');

      // Parsear respuesta según content-type
      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
      } else {
        responseData = (await response.text()) as unknown as T;
      }

      // Crear objeto de respuesta
      const apiResponse: ApiResponse<T> = {
        data: responseData,
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
      };

      // Lanzar error si el status no es exitoso
      if (!response.ok) {
        const error: ApiError = {
          message: this.getErrorMessage(response.status, responseData),
          status: response.status,
          statusText: response.statusText,
          data: responseData,
        };
        throw error;
      }

      return apiResponse;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        const timeoutError: ApiError = {
          message: 'Request timeout',
          status: 408,
          statusText: 'Request Timeout',
        };
        throw timeoutError;
      }

      if (error instanceof Error && error.message === 'Failed to fetch') {
        const networkError: ApiError = {
          message: 'Network error - please check your connection',
          status: 0,
          statusText: 'Network Error',
        };
        throw networkError;
      }

      // Re-throw ApiError instances
      if (this.isApiError(error)) {
        throw error;
      }

      // Wrap unknown errors
      const unknownError: ApiError = {
        message:
          error instanceof Error ? error.message : 'Unknown error occurred',
        status: 500,
        statusText: 'Internal Server Error',
      };
      throw unknownError;
    }
  }

  /**
   * GET request
   */
  async get<T>(
    endpoint: string,
    queryParams?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>('GET', endpoint, { queryParams });
  }

  /**
   * POST request
   */
  async post<T>(
    endpoint: string,
    body?: any,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>('POST', endpoint, { body, headers });
  }

  /**
   * PUT request
   */
  async put<T>(
    endpoint: string,
    body?: any,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>('PUT', endpoint, { body, headers });
  }

  /**
   * DELETE request
   */
  async delete<T>(
    endpoint: string,
    queryParams?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>('DELETE', endpoint, { queryParams });
  }

  /**
   * PATCH request
   */
  async patch<T>(
    endpoint: string,
    body?: any,
    headers?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    return this.request<T>('PATCH', endpoint, { body, headers });
  }

  /**
   * Generar mensaje de error basado en status HTTP
   */
  private getErrorMessage(status: number, data?: any): string {
    // Si el servidor envía un mensaje específico, usarlo
    if (data && typeof data === 'object' && data.message) {
      return data.message;
    }

    // Mensajes por defecto según status HTTP
    switch (status) {
      case 400:
        return 'Bad request - please check your data';
      case 401:
        return 'Unauthorized access';
      case 403:
        return 'Forbidden - you do not have permission';
      case 404:
        return 'Resource not found';
      case 410:
        return 'Resource no longer available';
      case 422:
        return 'Validation error - please check your data';
      case 429:
        return 'Too many requests - please try again later';
      case 500:
        return 'Internal server error - please try again';
      case 502:
        return 'Bad gateway - service temporarily unavailable';
      case 503:
        return 'Service unavailable - please try again later';
      default:
        return `HTTP error ${status}`;
    }
  }

  /**
   * Type guard para ApiError
   */
  private isApiError(error: any): error is ApiError {
    return (
      error &&
      typeof error === 'object' &&
      typeof error.message === 'string' &&
      typeof error.status === 'number'
    );
  }

  /**
   * Configurar headers adicionales
   */
  setHeader(key: string, value: string): void {
    this.defaultHeaders[key] = value;
  }

  /**
   * Remover header
   */
  removeHeader(key: string): void {
    delete this.defaultHeaders[key];
  }

  /**
   * Obtener configuración actual
   */
  getConfig(): {
    baseURL: string;
    timeout: number;
    headers: Record<string, string>;
  } {
    return {
      baseURL: this.baseURL,
      timeout: this.timeout,
      headers: { ...this.defaultHeaders },
    };
  }

  /**
   * Crear link de pago para una factura
   */
  async createPaymentLink(invoiceId: number): Promise<
    ApiResponse<{
      payment_url: string;
      expires_at: string;
    }>
  > {
    return this.post('/payment/create-link/', { invoice_id: invoiceId });
  }

  /**
   * Obtener métodos de pago disponibles por UUID del enlace de pago
   */
  async getPaymentMethods(
    shortUuid: string
  ): Promise<
    ApiResponse<import('../../entities/PaymentMethods').PaymentMethodsResponse>
  > {
    return this.get(`/payment/methods/${shortUuid}/`);
  }
}
